import { useEffect, useState } from 'react'
import { Box, Flex, Text, VStack, Input, Modal, toaster, Spinner } from '@minetrace/ui'
import { LuPlus, LuPencil, LuChevronRight } from 'react-icons/lu'
import { ocdeCatalogService } from '../../../api/services'
import type {
  OcdeCategoryAdmin,
  OcdeQuestionAdmin,
  SaveOcdeCategoryRequest,
  SaveOcdeQuestionRequest,
} from '../../../api/services/maestros/ocde.service'
import { themeColors as T } from '../../../theme'

type CategoryForm = SaveOcdeCategoryRequest & { id?: number }
type QuestionForm = SaveOcdeQuestionRequest & { id?: number }

const emptyCategory = (sortOrder: number): CategoryForm => ({ code: '', name: '', sortOrder, isActive: true })
const emptyQuestion = (categoryId: number, sortOrder: number): QuestionForm => ({
  categoryId, code: '', text: '', evaluatorHint: '', sortOrder, isActive: true,
})

function PillToggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <Box
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      w="44px" h="24px" borderRadius="full" flexShrink={0}
      bg={checked ? '#1d6664' : '#cbd5e1'} cursor="pointer" position="relative"
    >
      <Box position="absolute" top="3px" left={checked ? '23px' : '3px'} w="18px" h="18px" borderRadius="full" bg="white" shadow="0 1px 2px rgba(0,0,0,0.25)" />
    </Box>
  )
}

export default function OcdeMaestrosPage() {
  const [catalog, setCatalog] = useState<OcdeCategoryAdmin[]>([])
  const [loading, setLoading] = useState(true)
  const [expanded, setExpanded] = useState<Set<number>>(new Set())
  const [saving, setSaving] = useState(false)

  const [categoryForm, setCategoryForm] = useState<CategoryForm | null>(null)
  const [questionForm, setQuestionForm] = useState<QuestionForm | null>(null)

  const load = async () => {
    setLoading(true)
    try {
      const data = await ocdeCatalogService.getCatalog()
      setCatalog(data)
    } catch {
      toaster.error({ description: 'No se pudo cargar el catálogo OCDE' })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  const toggleExpand = (id: number) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const saveCategory = async () => {
    if (!categoryForm) return
    if (!categoryForm.name.trim()) { toaster.error({ description: 'El nombre es requerido' }); return }
    setSaving(true)
    try {
      const payload: SaveOcdeCategoryRequest = {
        code: categoryForm.code, name: categoryForm.name, sortOrder: categoryForm.sortOrder, isActive: categoryForm.isActive,
      }
      if (categoryForm.id) await ocdeCatalogService.updateCategory(categoryForm.id, payload)
      else await ocdeCatalogService.createCategory(payload)
      toaster.success({ description: 'Categoría guardada' })
      setCategoryForm(null)
      await load()
    } catch {
      toaster.error({ description: 'No se pudo guardar la categoría' })
    } finally { setSaving(false) }
  }

  const saveQuestion = async () => {
    if (!questionForm) return
    if (!questionForm.text.trim()) { toaster.error({ description: 'El texto es requerido' }); return }
    setSaving(true)
    try {
      const payload: SaveOcdeQuestionRequest = {
        categoryId: questionForm.categoryId, code: questionForm.code, text: questionForm.text,
        evaluatorHint: questionForm.evaluatorHint || null, sortOrder: questionForm.sortOrder, isActive: questionForm.isActive,
      }
      if (questionForm.id) await ocdeCatalogService.updateQuestion(questionForm.id, payload)
      else await ocdeCatalogService.createQuestion(payload)
      toaster.success({ description: 'Pregunta guardada' })
      setQuestionForm(null)
      await load()
    } catch {
      toaster.error({ description: 'No se pudo guardar la pregunta' })
    } finally { setSaving(false) }
  }

  const ActionButton = ({ label, icon, onClick, primary }: { label: string; icon?: React.ReactNode; onClick: () => void; primary?: boolean }) => (
    <Box
      role="button" onClick={onClick}
      display="inline-flex" alignItems="center" gap={1.5}
      px={3} h="34px" borderRadius="lg" fontSize="sm" fontWeight="600" cursor="pointer"
      bg={primary ? '#1d6664' : 'transparent'} color={primary ? 'white' : '#1d6664'}
      border={primary ? 'none' : '1px solid #1d6664'}
      _hover={{ opacity: 0.9 }}
    >
      {icon}{label}
    </Box>
  )

  return (
    <VStack gap={5} alignItems="stretch">
      <Flex justifyContent="space-between" alignItems="center" gap={3} flexWrap="wrap">
        <Box>
          <Text fontSize="2xl" fontWeight="700" color={T.text}>Cumplimiento Normativo</Text>
          <Text fontSize="sm" color={T.textSecondary}>Catálogo de categorías y preguntas del cuestionario de cumplimiento normativo de visita de campo</Text>
        </Box>
        <ActionButton label="Agregar categoría" icon={<LuPlus size={16} />} primary
          onClick={() => setCategoryForm(emptyCategory(catalog.length + 1))} />
      </Flex>

      {loading ? (
        <Flex justifyContent="center" p={10}><Spinner /></Flex>
      ) : (
        <VStack gap={3} alignItems="stretch">
          {catalog.map((cat) => {
            const isOpen = expanded.has(cat.id)
            return (
              <Box key={cat.id} bg="white" border="1px solid #e8dfd1" borderRadius="xl" overflow="hidden">
                <Flex alignItems="center" justifyContent="space-between" gap={3} p={4}>
                  <Flex alignItems="center" gap={2} flex={1} minW={0} cursor="pointer" onClick={() => toggleExpand(cat.id)}>
                    <Box transform={isOpen ? 'rotate(90deg)' : 'rotate(0deg)'} color={T.textSecondary} display="flex"><LuChevronRight size={18} /></Box>
                    <Text fontSize="sm" fontWeight="700" color={T.text}>{cat.code}. {cat.name}</Text>
                    {!cat.isActive && (
                      <Box px={2} py="2px" borderRadius="full" fontSize="11px" fontWeight="700" bg="#fae1db" color="#9a3020">Inactiva</Box>
                    )}
                    <Text fontSize="xs" color={T.textSecondary}>({cat.questions.length})</Text>
                  </Flex>
                  <Flex gap={2} flexShrink={0}>
                    <ActionButton label="Pregunta" icon={<LuPlus size={14} />}
                      onClick={() => setQuestionForm(emptyQuestion(cat.id, cat.questions.length + 1))} />
                    <ActionButton label="Editar" icon={<LuPencil size={14} />}
                      onClick={() => setCategoryForm({ id: cat.id, code: cat.code, name: cat.name, sortOrder: cat.sortOrder, isActive: cat.isActive })} />
                  </Flex>
                </Flex>

                {isOpen && (
                  <Box px={4} pb={3} borderTop="1px solid #f1f5f9">
                    {cat.questions.length === 0 ? (
                      <Text fontSize="sm" color={T.textSecondary} py={3}>Sin preguntas</Text>
                    ) : cat.questions.map((q: OcdeQuestionAdmin) => (
                      <Flex key={q.id} alignItems="flex-start" justifyContent="space-between" gap={3} py={3} borderBottom="1px solid #f8fafc">
                        <Text fontSize="sm" color={T.text} lineHeight={1.5}>
                          <Box as="span" fontWeight="700" color={T.textSecondary} mr={1.5}>{q.code}</Box>
                          {q.text}
                          {!q.isActive && <Box as="span" ml={2} fontSize="11px" fontWeight="700" color="#9a3020">(inactiva)</Box>}
                        </Text>
                        <Box role="button" onClick={() => setQuestionForm({
                          id: q.id, categoryId: q.categoryId, code: q.code, text: q.text,
                          evaluatorHint: q.evaluatorHint ?? '', sortOrder: q.sortOrder, isActive: q.isActive,
                        })} color="#1d6664" cursor="pointer" flexShrink={0} mt="2px"><LuPencil size={15} /></Box>
                      </Flex>
                    ))}
                  </Box>
                )}
              </Box>
            )
          })}
        </VStack>
      )}

      {/* Category modal */}
      <Modal
        open={categoryForm !== null}
        onClose={() => setCategoryForm(null)}
        title={categoryForm?.id ? 'Editar categoría' : 'Nueva categoría'}
        size="md"
        footer={
          <Box role="button" onClick={saveCategory} px={4} py={2} borderRadius="lg" fontSize="sm" fontWeight="700"
            bg="#b4532a" color="white" cursor={saving ? 'not-allowed' : 'pointer'} opacity={saving ? 0.6 : 1}>
            Guardar
          </Box>
        }
      >
        {categoryForm && (
          <VStack gap={3} alignItems="stretch">
            <Box>
              <Text fontSize="sm" fontWeight="600" color={T.text} mb={1}>Código</Text>
              <Input value={categoryForm.code} onChange={(e) => setCategoryForm({ ...categoryForm, code: e.target.value })} placeholder="Ej. 7" />
            </Box>
            <Box>
              <Text fontSize="sm" fontWeight="600" color={T.text} mb={1}>Nombre *</Text>
              <Input value={categoryForm.name} onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })} />
            </Box>
            <Box>
              <Text fontSize="sm" fontWeight="600" color={T.text} mb={1}>Orden</Text>
              <Input type="number" value={String(categoryForm.sortOrder)} onChange={(e) => setCategoryForm({ ...categoryForm, sortOrder: Number(e.target.value) || 0 })} />
            </Box>
            <Flex alignItems="center" justifyContent="space-between">
              <Text fontSize="sm" fontWeight="600" color={T.text}>Activa</Text>
              <PillToggle checked={categoryForm.isActive} onChange={(v) => setCategoryForm({ ...categoryForm, isActive: v })} />
            </Flex>
          </VStack>
        )}
      </Modal>

      {/* Question modal */}
      <Modal
        open={questionForm !== null}
        onClose={() => setQuestionForm(null)}
        title={questionForm?.id ? 'Editar pregunta' : 'Nueva pregunta'}
        size="md"
        footer={
          <Box role="button" onClick={saveQuestion} px={4} py={2} borderRadius="lg" fontSize="sm" fontWeight="700"
            bg="#b4532a" color="white" cursor={saving ? 'not-allowed' : 'pointer'} opacity={saving ? 0.6 : 1}>
            Guardar
          </Box>
        }
      >
        {questionForm && (
          <VStack gap={3} alignItems="stretch">
            <Box>
              <Text fontSize="sm" fontWeight="600" color={T.text} mb={1}>Código</Text>
              <Input value={questionForm.code} onChange={(e) => setQuestionForm({ ...questionForm, code: e.target.value })} placeholder="Ej. 7.1" />
            </Box>
            <Box>
              <Text fontSize="sm" fontWeight="600" color={T.text} mb={1}>Pregunta *</Text>
              <Box as="textarea" value={questionForm.text}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setQuestionForm({ ...questionForm, text: e.target.value })}
                minH="80px" w="100%" p={3} borderRadius="lg" border="1px solid #cbd5e1" fontSize="sm" />
            </Box>
            <Box>
              <Text fontSize="sm" fontWeight="600" color={T.text} mb={1}>Guía para el evaluador (opcional)</Text>
              <Box as="textarea" value={questionForm.evaluatorHint ?? ''}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setQuestionForm({ ...questionForm, evaluatorHint: e.target.value })}
                minH="60px" w="100%" p={3} borderRadius="lg" border="1px solid #cbd5e1" fontSize="sm" />
            </Box>
            <Box>
              <Text fontSize="sm" fontWeight="600" color={T.text} mb={1}>Orden</Text>
              <Input type="number" value={String(questionForm.sortOrder)} onChange={(e) => setQuestionForm({ ...questionForm, sortOrder: Number(e.target.value) || 0 })} />
            </Box>
            <Flex alignItems="center" justifyContent="space-between">
              <Text fontSize="sm" fontWeight="600" color={T.text}>Activa</Text>
              <PillToggle checked={questionForm.isActive} onChange={(v) => setQuestionForm({ ...questionForm, isActive: v })} />
            </Flex>
          </VStack>
        )}
      </Modal>
    </VStack>
  )
}
